import type { BigIntish } from '@morpho-org/blue-sdk'

import { toBigInt } from '@morpho-org/dapp-core'

import { isDustAllocation } from '@/helpers/dust'

// Temporary: mirrors VAULT_V2_DUST_FLOOR_USD / isDustAllocation in morpho-api
// services/consumer (#4733). Remove once these views read from the BFF instead of GraphQL.
// Fail open: hide a market only when every input is complete and it is certainly dust.
// See "Review decisions" in the #5734 description.

export interface VaultV2CapStackItem {
  absoluteCap: BigIntish
  relativeCap: BigIntish
  data?: {
    __typename: string
    adapterAddress?: string
    marketParams?: { collateralToken: string } | null
    market?: { marketId: string; collateralAsset?: { address: string } | null } | null
    collateralAddress?: string
    collateralToken?: { address: string } | null
  } | null
}

const isCapOpen = (cap: VaultV2CapStackItem | undefined): boolean =>
  !!cap && (toBigInt(cap.absoluteCap) ?? 0n) > 0n && (toBigInt(cap.relativeCap) ?? 0n) > 0n

const sameAddress = (a: string | undefined, b: string | undefined) =>
  !!a && !!b && a.toLowerCase() === b.toLowerCase()

/** The MarketV1 cap rows for `marketId`, one per adapter the market is configured through. */
export function getVaultV2MarketCaps<T extends VaultV2CapStackItem>(
  caps: readonly T[] | null | undefined,
  marketId: string
): T[] {
  return (caps ?? []).filter(
    cap => cap.data?.__typename === 'MarketV1CapData' && cap.data.market?.marketId === marketId
  )
}

/** For at least one adapter, the market, adapter and collateral caps of `marketId` are open. */
export function isVaultV2MarketFullyOpen(
  caps: readonly VaultV2CapStackItem[] | null | undefined,
  marketId: string
): boolean {
  const items = caps ?? []
  return getVaultV2MarketCaps(items, marketId).some(marketCap => {
    if (!isCapOpen(marketCap)) return false

    const adapterCap = items.find(
      cap =>
        cap.data?.__typename === 'AdapterCapData' &&
        sameAddress(cap.data.adapterAddress, marketCap.data?.adapterAddress)
    )
    if (!isCapOpen(adapterCap)) return false

    // Raw addresses first: the rich asset relations are null when the API can't resolve them.
    const collateralAddress =
      marketCap.data?.marketParams?.collateralToken ??
      marketCap.data?.market?.collateralAsset?.address
    if (!collateralAddress) return true
    return isCapOpen(
      items.find(
        cap =>
          cap.data?.__typename === 'CollateralCapData' &&
          sameAddress(
            cap.data.collateralAddress ?? cap.data.collateralToken?.address,
            collateralAddress
          )
      )
    )
  })
}

/**
 * Raw cap on dust, so a bad price feed can't make a large allocation read as dust. Residuals
 * start at 1 or 2 wei but keep earning interest, so 10 leaves room for high-APY markets while
 * capping the worst case under a broken price at about $0.01.
 */
export const VAULT_V2_DUST_MAX_ASSETS = 10n

/**
 * Dust is an exact zero, or at most VAULT_V2_DUST_MAX_ASSETS base units AND under $0.01.
 * An unknown allocation or USD value is never dust.
 */
export const isVaultV2DustAllocation = ({
  allocation,
  allocationUsd
}: {
  allocation: BigIntish | null | undefined
  allocationUsd: number | null | undefined
}): boolean => {
  const raw = toBigInt(allocation)
  if (raw == null) return false
  return raw === 0n || (raw <= VAULT_V2_DUST_MAX_ASSETS && isDustAllocation(allocationUsd))
}

/**
 * A Vault V2 market holding only dust with a closed cap stack, which a full deallocation
 * leaves behind.
 */
export const isVaultV2DustMarket = ({
  caps,
  marketId,
  allocation,
  allocationUsd
}: {
  caps: readonly VaultV2CapStackItem[] | null | undefined
  marketId: string
  allocation: BigIntish | null | undefined
  allocationUsd: number | null | undefined
}): boolean =>
  isVaultV2DustAllocation({ allocation, allocationUsd }) &&
  !isVaultV2MarketFullyOpen(caps, marketId)

/**
 * The market's cap rows cover every MorphoMarketV1 adapter of the vault. The API can drop an
 * adapter's 0/0 row while it still holds a position, so partial coverage means the summed
 * allocation is unknown.
 */
export const hasCompleteMarketCapCoverage = (
  marketCaps: readonly VaultV2CapStackItem[],
  adapters: readonly { __typename?: string; address?: string }[] | null | undefined
): boolean => {
  // No cap row, or no adapter list to compare against, is unknown.
  if (marketCaps.length === 0 || adapters == null) return false
  const coveredAdapters = new Set(
    marketCaps.map(cap => cap.data?.adapterAddress?.toLowerCase()).filter(Boolean)
  )
  // Match by address, not count: a former adapter's row must not stand in for an active one.
  return adapters
    .filter(adapter => adapter.__typename === 'MorphoMarketV1Adapter')
    .every(adapter => !!adapter.address && coveredAdapters.has(adapter.address.toLowerCase()))
}
